// src/components/dashboard/Painel.tsx
// Painel Principal — GRAIA v2

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    BookOpen, ArrowRight, CalendarDays,
    RotateCcw, AlertTriangle, Trophy, Users, ChevronRight,
    Star, TrendingUp, Activity, Crown, Medal, Award, GraduationCap, ClipboardList,
    Library, Lock, X, Construction
} from 'lucide-react';
import './Painel.css';
import { useFeatures } from '../../contexts/FeaturesContext';
import { useAuth } from '../../contexts/AuthContext';
import FragilitySubjectsModal from './FragilitySubjectsModal';

const API_BASE = '/api';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } });

interface PainelProps {
    onNavigate: (page: string, subTab?: string) => void;
    onShowToast?: (message: string, type: 'success' | 'error' | 'info') => void;
}

interface FixedEvent { _id: string; title: string; startTime: string; endTime: string; dayOfWeek: number; color?: string; location?: string; type?: string; customType?: string; }
interface MonthEvent { _id: string; title: string; date: string; color?: string; isCompleted?: boolean; }
interface ReviewItem { _id: string; content: string; nextReview: string; }
interface RankGroup { rank: number; groupId: string; groupName: string; groupScore: number; memberCount: number; }
interface TopDeck { deckId: string; deckName: string; cardsStudied: number; timeSpent: number; }
interface FragItem { name: string; errorRate: number; total: number; }
interface DifficultCard { cardId: string; front: string; deckName: string; subdeckName: string; lastRating: number; }
interface WeekDay { date: string; dayLabel: string; cardsStudied: number; isProductiveDay: boolean; }
interface MostActiveCycle { cycleId: string; cycleName: string; cardsStudied: number; }

const FEATURE_TAB_KEY: Record<string, string> = {
    questions: 'questions',
    stats: 'stats',
    planner: 'planner',
    groups: 'groups',
};

const FEATURE_LABELS: Record<string, string> = {
    questions: 'Questões',
    stats: 'Estatísticas',
    planner: 'Assistente Graia',
    groups: 'Grupos de Estudo',
};

const TODAY_DOW = new Date().getDay();
const MONTH_NOW = new Date().getMonth() + 1;
const YEAR_NOW = new Date().getFullYear();
const PAINEL_TTL = 2 * 60 * 1000;

const painelFromCache = (key: string) => {
    try {
        const raw = sessionStorage.getItem(`painel_${key}`);
        if (!raw) return null;
        const { data, timestamp } = JSON.parse(raw);
        if (Date.now() - timestamp > PAINEL_TTL) return null;
        return data;
    } catch { return null; }
};

const painelToCache = (key: string, data: any) => {
    try {
        sessionStorage.setItem(`painel_${key}`, JSON.stringify({ data, timestamp: Date.now() }));
    } catch { }
};

const EVENT_TYPE_MAP: Record<string, string> = {
    class: 'Aula', classes: 'Aula', course: 'Curso', tutoring: 'Monitoria',
    office_hours: 'Plantão de Dúvidas', individual_study: 'Estudo Individual',
    study: 'Estudo', review: 'Revisão', mock_exam: 'Simulado',
    questions: 'Questões', study_group: 'Grupo de Estudo', research: 'Pesquisa',
    reading: 'Leitura', work: 'Trabalho', project: 'Projeto', meeting: 'Reunião',
    planning: 'Planejamento', organization: 'Organização', gym: 'Academia',
    meditation: 'Meditação', rest: 'Descanso', family: 'Compromisso Familiar',
    social: 'Evento Social', appointment: 'Atendimento', sport: 'Esporte',
    sports: 'Esporte', personal: 'Pessoal', health: 'Saúde', leisure: 'Lazer', other: 'Outro',
};

function translateType(type?: string, customType?: string): string | undefined {
    if (!type) return undefined;
    if (type === 'other' && customType) return customType;
    return EVENT_TYPE_MAP[type.toLowerCase()] ?? type;
}

function reviewLabel(dateStr: string) {
    const diff = Math.ceil((new Date(dateStr).getTime() - new Date().setHours(0, 0, 0, 0)) / 86400000);
    if (diff <= 0) return { text: 'Hoje', urgent: true };
    if (diff === 1) return { text: 'Amanhã', urgent: false };
    return { text: `Em ${diff}d`, urgent: false };
}

function stripHtml(html: string): string {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
}

// ── Modal de Upgrade de Plano ─────────────────────────────────
const UpgradePlanModal: React.FC<{ isOpen: boolean; featureName?: string; onClose: () => void }> = ({ isOpen, featureName, onClose }) => {
    if (!isOpen) return null;
    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }} onClick={onClose}>
            <div style={{ background: 'var(--color-surface, #fff)', borderRadius: '18px', padding: '36px 32px 28px', maxWidth: '420px', width: '100%', textAlign: 'center', position: 'relative', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }} onClick={e => e.stopPropagation()}>
                <button onClick={onClose} style={{ position: 'absolute', top: '14px', right: '14px', background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted, #999)', padding: '4px', borderRadius: '6px', display: 'flex', alignItems: 'center' }}><X size={18} /></button>
                <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: 'var(--platform-a10)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', color: 'var(--platform-primary)' }}><Lock size={36} /></div>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: '0 0 8px', color: 'var(--color-text-primary, #1a1a1a)' }}>Funcionalidade bloqueada</h2>
                {featureName && <p style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--platform-primary)', margin: '0 0 12px', padding: '4px 14px', background: 'var(--platform-a08)', borderRadius: '20px', display: 'inline-block' }}>{featureName}</p>}
                <p style={{ fontSize: '0.88rem', color: 'var(--color-text-secondary, #666)', lineHeight: 1.55, margin: '0 0 24px' }}>Esta funcionalidade não está disponível no seu plano atual. Evolua para os planos <strong>Combo</strong> ou <strong>Acesso Total</strong> para desbloquear.</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <button onClick={() => { window.open('/catalogo', '_blank'); onClose(); }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '13px 24px', background: 'var(--platform-primary)', color: 'white', border: 'none', borderRadius: '10px', fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer' }}>Ver Planos no Catálogo <ArrowRight size={16} /></button>
                    <button onClick={onClose} style={{ padding: '11px 24px', background: 'transparent', color: 'var(--color-text-secondary, #666)', border: '1.5px solid var(--color-border, #e0e0e0)', borderRadius: '10px', fontSize: '0.9rem', fontWeight: 500, cursor: 'pointer' }}>Agora não</button>
                </div>
            </div>
        </div>
    );
};

// ── Modal de Em Obras ─────────────────────────────────────────
const InterdictedTabModal: React.FC<{ isOpen: boolean; featureName?: string; onClose: () => void }> = ({ isOpen, featureName, onClose }) => {
    if (!isOpen) return null;
    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }} onClick={onClose}>
            <div style={{ background: 'var(--color-surface, #fff)', borderRadius: '18px', padding: '36px 32px 28px', maxWidth: '420px', width: '100%', textAlign: 'center', position: 'relative', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }} onClick={e => e.stopPropagation()}>
                <button onClick={onClose} style={{ position: 'absolute', top: '14px', right: '14px', background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted, #999)', padding: '4px', borderRadius: '6px', display: 'flex', alignItems: 'center' }}><X size={18} /></button>
                <div style={{ marginBottom: '16px' }}><Construction size={64} strokeWidth={1.5} color="#D4B35D" /></div>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: '0 0 8px', color: 'var(--color-text-primary, #1a1a1a)' }}>Em Obras</h2>
                {featureName && <p style={{ fontSize: '0.9rem', fontWeight: 600, color: '#D4B35D', margin: '0 0 12px', padding: '4px 14px', background: 'rgba(212,179,93,0.1)', borderRadius: '20px', display: 'inline-block' }}>{featureName}</p>}
                <p style={{ fontSize: '0.88rem', color: 'var(--color-text-secondary, #666)', lineHeight: 1.55, margin: '0 0 8px' }}>Esta sessão está em obras e passando por melhorias.</p>
                <p style={{ fontSize: '0.84rem', color: 'var(--color-text-muted, #999)', lineHeight: 1.5, margin: '0 0 24px' }}>Estamos trabalhando para trazer a melhor experiência possível.</p>
                <button onClick={onClose} style={{ padding: '12px 32px', background: '#D4B35D', color: 'white', border: 'none', borderRadius: '10px', fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer', width: '100%' }}>Entendi</button>
            </div>
        </div>
    );
};

// ═══════════════════════════════════════════════════════════
const Painel: React.FC<PainelProps> = ({ onNavigate }) => {
    const navigate = useNavigate();

    const { canAccess, loading: featuresLoading } = useFeatures();
    const { user, isAdmin } = useAuth();

    const [upgradeModal, setUpgradeModal] = useState<{ open: boolean; featureName?: string }>({ open: false });
    const [interdictedModal, setInterdictedModal] = useState<{ open: boolean; featureName?: string }>({ open: false });
    const [interdictedTabs, setInterdictedTabs] = useState<Record<string, boolean>>({});
    const [showFragilityModal, setShowFragilityModal] = useState(false);

    useEffect(() => {
        fetch('/api/platform-config', auth())
            .then(r => r.ok ? r.json() : null)
            .then(d => { if (d?.interdictedTabs) setInterdictedTabs(d.interdictedTabs); })
            .catch(() => { });
    }, []);

    const guardedNavigate = useCallback((
        feature: string | null,
        featureName: string,
        page: string,
        subTab?: string
    ) => {
        if (isAdmin) { onNavigate(page, subTab); return; }
        const tabKey = feature ? FEATURE_TAB_KEY[feature] : null;
        if (tabKey && interdictedTabs[tabKey]) { setInterdictedModal({ open: true, featureName }); return; }
        if (featuresLoading) { onNavigate(page, subTab); return; }
        if (feature && !canAccess(feature as any)) { setUpgradeModal({ open: true, featureName }); return; }
        onNavigate(page, subTab);
    }, [isAdmin, interdictedTabs, featuresLoading, canAccess, onNavigate]);

    // ── Data states ───────────────────────────────────────────
    const [loading, setLoading] = useState(true);
    const [fixedEvents, setFixedEvents] = useState<FixedEvent[]>([]);
    const [monthEvents, setMonthEvents] = useState<MonthEvent[]>([]);
    const [weekReviews, setWeekReviews] = useState<ReviewItem[]>([]);
    const [pendingReviews, setPendingReviews] = useState<ReviewItem[]>([]);
    const [globalRanking, setGlobalRanking] = useState<RankGroup[]>([]);
    const [myGroups, setMyGroups] = useState<any[]>([]);
    const [topDecks, setTopDecks] = useState<TopDeck[]>([]);
    const [fragility, setFragility] = useState<FragItem[]>([]);
    const [difficultCards, setDifficultCards] = useState<DifficultCard[]>([]);
    const [weekHistory, setWeekHistory] = useState<WeekDay[]>([]);
    const [weekSummary, setWeekSummary] = useState<any>(null);
    const [allTime, setAllTime] = useState<any>(null);
    const [qStats, setQStats] = useState<any>(null);
    const [bestSubject, setBestSubject] = useState<string | null>(null);
    const [lastCycleId, setLastCycleId] = useState<string | null>(null);
    const [lastDeck, setLastDeck] = useState<any>(null);
    const [mostActiveCycle, setMostActiveCycle] = useState<MostActiveCycle | null>(null);

    const load = useCallback(async () => {
        const cachedMain = painelFromCache('main');
        const cachedWeek = painelFromCache('week');
        const cachedPlanner = painelFromCache('planner');

        if (cachedMain) {
            setWeekSummary(cachedMain.weekSummary);
            setAllTime(cachedMain.allTime);
            setTopDecks(cachedMain.topDecks || []);
            setFragility(cachedMain.fragility || []);
            setBestSubject(cachedMain.bestSubject || null);
            setDifficultCards(cachedMain.difficultCards || []);
            setGlobalRanking(cachedMain.globalRanking || []);
            setMyGroups(cachedMain.myGroups || []);
            setQStats(cachedMain.qStats || null);
            setLastCycleId(cachedMain.lastCycleId || null);
            setLastDeck(cachedMain.lastDeck || null);
            setMostActiveCycle(cachedMain.mostActiveCycle || null);
            setLoading(false);
        }
        if (cachedWeek) setWeekHistory(cachedWeek || []);
        if (cachedPlanner) {
            setFixedEvents(cachedPlanner.fixedEvents || []);
            setMonthEvents(cachedPlanner.monthEvents || []);
            setPendingReviews(cachedPlanner.pendingReviews || []);
            setWeekReviews(cachedPlanner.weekReviews || []);
        }

        if (!cachedMain) setLoading(true);

        const now = new Date(); now.setHours(0, 0, 0, 0);

        let _weekSummary: any = null, _allTime: any = null, _topDecks: any[] = [];
        let _fragility: any[] = [], _bestSubject: string | null = null;
        let _difficultCards: any[] = [], _globalRanking: any[] = [], _myGroups: any[] = [];
        let _qStats: any = null, _lastCycleId: string | null = null, _lastDeck: any = null;
        let _fixedEvents: any[] = [], _monthEvents: any[] = [];
        let _pendingReviews: any[] = [], _weekReviews: any[] = [], _weekHistory: any[] = [];
        let _mostActiveCycle: MostActiveCycle | null = null;

        const results = await Promise.allSettled([
            fetch(`${API_BASE}/planner/fixed-events`, auth()),                           // 0
            fetch(`${API_BASE}/planner/month-events?month=${MONTH_NOW}&year=${YEAR_NOW}`, auth()), // 1
            fetch(`${API_BASE}/planner/review-contents`, auth()),                        // 2
            fetch(`${API_BASE}/groups/global/ranking`, auth()),                          // 3
            fetch(`${API_BASE}/groups/my-groups`, auth()),                               // 4
            fetch(`${API_BASE}/stats/top-decks-month`, auth()),                          // 5
            fetch(`${API_BASE}/question-stats/fragility-index`, auth()),                 // 6
            fetch(`${API_BASE}/stats/difficult-cards-today`, auth()),                    // 7
            fetch(`${API_BASE}/stats/week-history`, auth()),                             // 8
            fetch(`${API_BASE}/stats/dashboard`, auth()),                                // 9
            fetch(`${API_BASE}/question-stats/overview?period=30`, auth()),              // 10
            fetch(`${API_BASE}/dashboard/me`, auth()),                                   // 11
            fetch(`${API_BASE}/stats/most-active-cycle?hours=48`, auth()),               // 12
        ]);

        const ok = (i: number) => results[i].status === 'fulfilled' && (results[i] as any).value.ok;
        const json = async (i: number) => (results[i] as any).value.json();

        if (ok(0)) { const d = await json(0); _fixedEvents = (d.events || []).filter((e: FixedEvent) => e.dayOfWeek === TODAY_DOW); setFixedEvents(_fixedEvents); }
        if (ok(1)) { const d = await json(1); _monthEvents = d.events || []; setMonthEvents(_monthEvents); }
        if (ok(2)) {
            const d = await json(2);
            const all: ReviewItem[] = (d.contents || []).filter((r: any) => r.nextReview);
            const pending = all.filter(r => new Date(r.nextReview) <= now);
            const in7days = new Date(now); in7days.setDate(now.getDate() + 7);
            const next7 = all.filter(r => { const dt = new Date(r.nextReview); return dt > now && dt <= in7days; }).slice(0, 6);
            _pendingReviews = pending.slice(0, 5); _weekReviews = next7;
            setPendingReviews(_pendingReviews); setWeekReviews(_weekReviews);
        }
        if (ok(3)) { const d = await json(3); _globalRanking = (d.ranking || []).slice(0, 3); setGlobalRanking(_globalRanking); }
        if (ok(4)) { const d = await json(4); _myGroups = d || []; setMyGroups(_myGroups); }
        if (ok(5)) { const d = await json(5); _topDecks = d.topDecks || []; setTopDecks(_topDecks); }
        if (ok(6)) {
            const d = await json(6);
            const items: FragItem[] = d.fragility || [];
            _fragility = items.slice(0, 3); setFragility(_fragility);
            const best = [...items].sort((a, b) => a.errorRate - b.errorRate).find(i => i.total >= 5);
            _bestSubject = best ? best.name : null;
            if (_bestSubject) setBestSubject(_bestSubject);
        }
        if (ok(7)) { const d = await json(7); _difficultCards = (d.difficultCards || []).slice(0, 3); setDifficultCards(_difficultCards); }
        if (ok(8)) { const d = await json(8); _weekHistory = d.history || []; setWeekHistory(_weekHistory); }
        if (ok(9)) { const d = await json(9); _weekSummary = d.week; _allTime = d.allTime; setWeekSummary(_weekSummary); setAllTime(_allTime); }
        if (ok(10)) { const d = await json(10); _qStats = d.overview; setQStats(_qStats); }
        if (ok(11)) {
            const d = await json(11);
            for (const c of (d.cycles || [])) {
                if (c.purchased && c.decks?.length > 0) {
                    _lastCycleId = c._id; _lastDeck = c.decks[0];
                    setLastCycleId(_lastCycleId); setLastDeck(_lastDeck);
                    break;
                }
            }
        }
        if (ok(12)) {
            const d = await json(12);
            if (d.cycleId) { _mostActiveCycle = { cycleId: d.cycleId, cycleName: d.cycleName, cardsStudied: d.cardsStudied }; setMostActiveCycle(_mostActiveCycle); }
        }

        painelToCache('main', { weekSummary: _weekSummary, allTime: _allTime, topDecks: _topDecks, fragility: _fragility, bestSubject: _bestSubject, difficultCards: _difficultCards, globalRanking: _globalRanking, myGroups: _myGroups, qStats: _qStats, lastCycleId: _lastCycleId, lastDeck: _lastDeck, mostActiveCycle: _mostActiveCycle });
        painelToCache('week', _weekHistory);
        painelToCache('planner', { fixedEvents: _fixedEvents, monthEvents: _monthEvents, pendingReviews: _pendingReviews, weekReviews: _weekReviews });
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);


    if (loading) return (
        <div className="pnldsb2-loading">
            <div className="pnldsb2-spinner" />
            <span>Carregando painel...</span>
        </div>
    );

    const weekMax = Math.max(...weekHistory.map(d => d.cardsStudied), 1);
    const bestDeck = topDecks[0] ?? null;
    const isNewStudent = (allTime?.totalCardsStudied ?? 1) === 0 && (qStats?.totalResolved ?? 1) === 0;

    const rankIcon = (rank: number) => {
        if (rank === 1) return <Crown size={17} className="pnldsb2-rank-icon pnldsb2-rank-icon--1" />;
        if (rank === 2) return <Medal size={17} className="pnldsb2-rank-icon pnldsb2-rank-icon--2" />;
        return <Award size={17} className="pnldsb2-rank-icon pnldsb2-rank-icon--3" />;
    };

    // Card 1: ciclo a exibir
    const card1CycleId = mostActiveCycle?.cycleId ?? lastCycleId;
    const card1CycleName = mostActiveCycle?.cycleName ?? null;
    const card1Cards48h = mostActiveCycle?.cardsStudied ?? null;

    // Saudação personalizada
    const hour = new Date().getHours();
    const greeting = hour < 12 ? 'Bom dia' : hour < 18 ? 'Boa tarde' : 'Boa noite';

    return (
        <div className="pnldsb2-root">
            <UpgradePlanModal isOpen={upgradeModal.open} featureName={upgradeModal.featureName} onClose={() => setUpgradeModal({ open: false })} />
            <InterdictedTabModal isOpen={interdictedModal.open} featureName={interdictedModal.featureName} onClose={() => setInterdictedModal({ open: false })} />
            <FragilitySubjectsModal isOpen={showFragilityModal} onClose={() => setShowFragilityModal(false)} />

            {/* ── PARTE 1: Título com saudação ─────────────────── */}
            <div className="pnldsb2-page-title">
                <div className="pnldsb2-page-title-text">
                    <h1>Painel Principal</h1>
                    <p className="pnldsb2-page-subtitle">
                        {greeting}, {user?.username ?? 'estudante'}. O que vamos estudar hoje?
                    </p>
                </div>
            </div>

            {isNewStudent ? (
                <div className="pnldsb2-ns-root">
                    <div className="pnldsb2-ns-hero">
                        <button className="pnldsb2-ns-card pnldsb2-ns-card--deck"
                            onClick={() => lastCycleId ? navigate(`/cycles/${lastCycleId}/decks`) : onNavigate('explore', 'content')}>
                            <div className="pnldsb2-ns-icon"><GraduationCap size={36} /></div>
                            <div className="pnldsb2-ns-body">
                                <span className="pnldsb2-ns-title">Estudar flashcards</span>
                                <span className="pnldsb2-ns-sub">Explore os decks disponíveis</span>
                            </div>
                            <ArrowRight size={22} className="pnldsb2-ns-arrow" />
                        </button>
                        <button className="pnldsb2-ns-card pnldsb2-ns-card--questions"
                            onClick={() => guardedNavigate('questions', FEATURE_LABELS.questions, 'questions', 'study')}>
                            <div className="pnldsb2-ns-icon"><ClipboardList size={36} /></div>
                            <div className="pnldsb2-ns-body">
                                <span className="pnldsb2-ns-title">Responder questões</span>
                                <span className="pnldsb2-ns-sub">Banco de questões de vestibulares</span>
                            </div>
                            <ArrowRight size={22} className="pnldsb2-ns-arrow" />
                        </button>
                    </div>
                    <div className="pnldsb2-ns-extras">
                        <div className="pnldsb2-card pnldsb2-ns-extra">
                            <div className="pnldsb2-ns-extra-icon"><CalendarDays size={22} /></div>
                            <div className="pnldsb2-ns-extra-body">
                                <span className="pnldsb2-ns-extra-title">Planner de estudos</span>
                                <span className="pnldsb2-ns-extra-sub">Organize seu cronograma quando quiser</span>
                            </div>
                            <button className="pnldsb2-card-link" onClick={() => guardedNavigate('planner', FEATURE_LABELS.planner, 'planner')}>Abrir <ChevronRight size={12} /></button>
                        </div>
                        <div className="pnldsb2-card pnldsb2-ns-extra">
                            <div className="pnldsb2-ns-extra-icon"><Users size={22} /></div>
                            <div className="pnldsb2-ns-extra-body">
                                <span className="pnldsb2-ns-extra-title">Grupos de estudo</span>
                                <span className="pnldsb2-ns-extra-sub">Você pode criar ou entrar em um grupo quando quiser</span>
                            </div>
                            <button className="pnldsb2-card-link" onClick={() => guardedNavigate('groups', FEATURE_LABELS.groups, 'groups')}>Explorar <ChevronRight size={12} /></button>
                        </div>
                    </div>
                </div>
            ) : (
            <>
            {/* ── PARTE 2: Blocos compostos de Acesso Rápido ──── */}
            <div className="pnldsb2-quick-row">

                {/* ── Bloco Esquerdo — Flashcards ── */}
                <div className="pnldsb2-quick-card pnldsb2-quick-card--deck pnldsb2-quick-card--compound">

                    {/* Seção 1: Campo mais ativo em 48h */}
                    <button
                        className="pnldsb2-qc-section pnldsb2-qc-section--clickable pnldsb2-qc-section--first"
                        onClick={() => card1CycleId ? navigate(`/cycles/${card1CycleId}/decks`) : onNavigate('explore', 'content')}
                    >
                        <span className="pnldsb2-qc-badge">Acesso rápido</span>
                        <div className="pnldsb2-qc-row">
                            <div className="pnldsb2-quick-icon"><GraduationCap size={26} /></div>
                            <div className="pnldsb2-quick-body">
                                <span className="pnldsb2-quick-title">
                                    {card1CycleName ?? (lastDeck?.name ?? 'Flashcards')}
                                </span>
                                <span className="pnldsb2-quick-sub">
                                    {card1Cards48h !== null
                                        ? `${card1Cards48h} cards em 2 dias`
                                        : (lastDeck ? lastDeck.name : 'Explorar decks')}
                                </span>
                            </div>
                            <ArrowRight size={16} className="pnldsb2-quick-arrow" />
                        </div>
                    </button>

                    <div className="pnldsb2-qc-divider" />

                    {/* Seção 2: Campos de Estudo */}
                    <button
                        className="pnldsb2-qc-section pnldsb2-qc-section--clickable"
                        onClick={() => onNavigate('explore', 'content')}
                    >
                        <div className="pnldsb2-qc-row">
                            <div className="pnldsb2-quick-icon pnldsb2-quick-icon--sm"><Library size={20} /></div>
                            <div className="pnldsb2-quick-body">
                                <span className="pnldsb2-quick-title">Campos de Estudo</span>
                                <span className="pnldsb2-quick-sub">Explorar todos os decks disponíveis</span>
                            </div>
                            <ArrowRight size={16} className="pnldsb2-quick-arrow" />
                        </div>
                    </button>

                    <div className="pnldsb2-qc-divider" />

                    {/* Seção 3: Flashcards para Revisar */}
                    <div className="pnldsb2-qc-section">
                        <p className="pnldsb2-qc-label">
                            <BookOpen size={12} />
                            Flashcards para Revisar
                        </p>
                        {difficultCards.length === 0 ? (
                            <div className="pnldsb2-qc-empty">
                                <Star size={13} />
                                <span>Nenhum card difícil hoje</span>
                            </div>
                        ) : (
                            <div className="pnldsb2-prio-list">
                                {difficultCards.map(card => (
                                    <div key={card.cardId} className="pnldsb2-prio-item">
                                        <div className="pnldsb2-prio-dot" data-r={card.lastRating} />
                                        <div className="pnldsb2-prio-body">
                                            <span className="pnldsb2-prio-text">
                                                {(() => { const p = stripHtml(card.front); return p.length > 52 ? p.slice(0, 52) + '…' : p; })()}
                                            </span>
                                            <span className="pnldsb2-prio-meta">{card.deckName} · {card.subdeckName}</span>
                                        </div>
                                        <span className="pnldsb2-prio-rating">nota {card.lastRating}</span>
                                    </div>
                                ))}
                                {lastCycleId && (
                                    <button className="pnldsb2-prio-cta" onClick={() => navigate(`/cycles/${lastCycleId}/decks`)}>
                                        Revisar agora <ArrowRight size={13} />
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Bloco Direito — Questões ── */}
                <div className="pnldsb2-quick-card pnldsb2-quick-card--questions pnldsb2-quick-card--compound">

                    {/* Seção 1: Questões */}
                    <button
                        className="pnldsb2-qc-section pnldsb2-qc-section--clickable pnldsb2-qc-section--first"
                        onClick={() => onNavigate('questions', 'study')}
                    >
                        <span className="pnldsb2-qc-badge">Acesso rápido</span>
                        <div className="pnldsb2-qc-row">
                            <div className="pnldsb2-quick-icon"><ClipboardList size={26} /></div>
                            <div className="pnldsb2-quick-body">
                                <span className="pnldsb2-quick-title">Questões</span>
                                <span className="pnldsb2-quick-sub">
                                    {qStats ? `${qStats.totalResolved} respondidas · ${qStats.accuracy}% precisão` : 'Banco de questões'}
                                </span>
                            </div>
                            <ArrowRight size={16} className="pnldsb2-quick-arrow" />
                        </div>
                    </button>

                    <div className="pnldsb2-qc-divider" />

                    {/* Seção 2: Provas e Simulados */}
                    <button
                        className="pnldsb2-qc-section pnldsb2-qc-section--clickable"
                        onClick={() => guardedNavigate('questions', FEATURE_LABELS.questions, 'provas')}
                    >
                        <div className="pnldsb2-qc-row">
                            <div className="pnldsb2-quick-icon pnldsb2-quick-icon--sm"><Award size={20} /></div>
                            <div className="pnldsb2-quick-body">
                                <span className="pnldsb2-quick-title">Provas e Simulados</span>
                                <span className="pnldsb2-quick-sub">Simulados e provas anteriores</span>
                            </div>
                            <ArrowRight size={16} className="pnldsb2-quick-arrow" />
                        </div>
                    </button>

                    <div className="pnldsb2-qc-divider" />

                    {/* Seção 3: Assuntos Frágeis */}
                    <div className="pnldsb2-qc-section">
                        <div className="pnldsb2-frag-label-row">
                            <p className="pnldsb2-qc-label">
                                <AlertTriangle size={12} />
                                Assuntos Frágeis
                            </p>
                            <button
                                className="pnldsb2-frag-ver-mais"
                                onClick={() => setShowFragilityModal(true)}
                            >
                                Ver mais <ArrowRight size={11} />
                            </button>
                        </div>
                        {fragility.length === 0 ? (
                            <div className="pnldsb2-qc-empty">
                                <span>Responda questões para análise</span>
                            </div>
                        ) : (
                            <div className="pnldsb2-prio-list">
                                {fragility.map((item, i) => (
                                    <div key={i} className="pnldsb2-frag-item">
                                        <div className="pnldsb2-frag-header">
                                            <span className="pnldsb2-frag-name">
                                                {item.name.length > 40 ? item.name.slice(0, 40) + '…' : item.name}
                                            </span>
                                            <span className="pnldsb2-frag-pct">{item.errorRate}%</span>
                                        </div>
                                        <div className="pnldsb2-frag-track">
                                            <div className="pnldsb2-frag-fill" style={{ width: `${item.errorRate}%` }} />
                                        </div>
                                    </div>
                                ))}
                                <button className="pnldsb2-prio-cta"
                                    onClick={() => guardedNavigate('questions', FEATURE_LABELS.questions, 'questions', 'study')}>
                                    Treinar assuntos frágeis <ArrowRight size={13} />
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Planner row ───────────────────────────────────────── */}
            <div className="pnldsb2-planner-row">
                <div className="pnldsb2-card pnldsb2-today-schedule">
                    <div className="pnldsb2-card-header">
                        <CalendarDays size={15} />
                        <span>Hoje — {new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'short' })}</span>
                        <button className="pnldsb2-card-link" onClick={() => guardedNavigate('planner', FEATURE_LABELS.planner, 'planner')}>Ver planner <ChevronRight size={12} /></button>
                    </div>
                    {fixedEvents.length === 0 ? (
                        <div className="pnldsb2-empty">
                            <CalendarDays size={32} /><span>Nenhum evento fixo hoje</span>
                            <button className="pnldsb2-empty-cta" onClick={() => guardedNavigate('planner', FEATURE_LABELS.planner, 'planner')}>Adicionar no Planner</button>
                        </div>
                    ) : (
                        <div className="pnldsb2-schedule-list">
                            {[...fixedEvents].sort((a, b) => a.startTime.localeCompare(b.startTime)).map(ev => (
                                <div key={ev._id} className="pnldsb2-schedule-item" style={{ '--ev-color': ev.color || 'var(--platform-primary)' } as any}>
                                    <div className="pnldsb2-schedule-bar" />
                                    <div className="pnldsb2-schedule-time">
                                        <span>{ev.startTime.slice(0, 5)}</span>
                                        <span className="pnldsb2-schedule-end">{ev.endTime.slice(0, 5)}</span>
                                    </div>
                                    <div className="pnldsb2-schedule-info">
                                        <span className="pnldsb2-schedule-title">{ev.title}</span>
                                        {ev.location && <span className="pnldsb2-schedule-loc">{ev.location}</span>}
                                    </div>
                                    {ev.type && <span className="pnldsb2-schedule-tag">{translateType(ev.type, ev.customType)}</span>}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="pnldsb2-planner-right">
                    <div className="pnldsb2-card pnldsb2-month-events">
                        <div className="pnldsb2-card-header">
                            <CalendarDays size={15} /><span>Eventos do Mês</span>
                            <button className="pnldsb2-card-link" onClick={() => guardedNavigate('planner', FEATURE_LABELS.planner, 'planner')}>Ver agenda <ChevronRight size={12} /></button>
                        </div>
                        {monthEvents.length === 0 ? (
                            <div className="pnldsb2-empty pnldsb2-empty--sm"><span>Sem eventos este mês</span></div>
                        ) : (
                            <div className="pnldsb2-month-list">
                                {monthEvents.slice(0, 6).map(ev => {
                                    const d = new Date(ev.date.slice(0, 10) + 'T12:00:00');
                                    return (
                                        <div key={ev._id} className={`pnldsb2-month-item ${ev.isCompleted ? 'pnldsb2-month-item--done' : ''}`}>
                                            <div className="pnldsb2-month-date">
                                                <span className="pnldsb2-month-day">{d.getDate()}</span>
                                                <span className="pnldsb2-month-mon">{d.toLocaleDateString('pt-BR', { month: 'short' })}</span>
                                            </div>
                                            <div className="pnldsb2-month-dot" style={{ background: ev.color || 'var(--platform-primary)' }} />
                                            <span className="pnldsb2-month-title">{ev.title}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div className="pnldsb2-card pnldsb2-reviews">
                        <div className="pnldsb2-card-header">
                            <RotateCcw size={15} /><span>Revisões Programadas</span>
                            <button className="pnldsb2-card-link" onClick={() => guardedNavigate('planner', FEATURE_LABELS.planner, 'planner')}>Ver todas <ChevronRight size={12} /></button>
                        </div>
                        {pendingReviews.length === 0 && weekReviews.length === 0 ? (
                            <div className="pnldsb2-empty pnldsb2-empty--sm"><span>Nenhuma revisão programada</span></div>
                        ) : (
                            <div className="pnldsb2-reviews-list">
                                {pendingReviews.map(r => (
                                    <div key={r._id} className="pnldsb2-review-item pnldsb2-review-item--pending">
                                        <span className="pnldsb2-review-tag pnldsb2-review-tag--pending">Pendente</span>
                                        <span className="pnldsb2-review-text">{r.content.length > 42 ? r.content.slice(0, 42) + '…' : r.content}</span>
                                    </div>
                                ))}
                                {weekReviews.map(r => {
                                    const lbl = reviewLabel(r.nextReview);
                                    return (
                                        <div key={r._id} className="pnldsb2-review-item">
                                            <span className={`pnldsb2-review-tag ${lbl.urgent ? 'pnldsb2-review-tag--soon' : ''}`}>{lbl.text}</span>
                                            <span className="pnldsb2-review-text">{r.content.length > 42 ? r.content.slice(0, 42) + '…' : r.content}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Performance Geral ─────────────────────────────────── */}
            <div className="pnldsb2-card">
                <div className="pnldsb2-card-header">
                    <TrendingUp size={15} /><span>Performance Geral</span>
                    <button className="pnldsb2-card-link" onClick={() => guardedNavigate('stats', FEATURE_LABELS.stats, 'stats')}>Detalhes <ChevronRight size={12} /></button>
                </div>
                <div className="pnldsb2-perf-grid">
                    <div className="pnldsb2-perf-item pnldsb2-perf-item--flashcards"><span className="pnldsb2-perf-val">{allTime?.totalCardsStudied ?? 0}</span><span className="pnldsb2-perf-lbl">Flashcards total</span></div>
                    <div className="pnldsb2-perf-item pnldsb2-perf-item--flashcards"><span className="pnldsb2-perf-val">{allTime?.masteryPercentage ?? 0}%</span><span className="pnldsb2-perf-lbl">Domínio geral</span></div>
                    <div className="pnldsb2-perf-item pnldsb2-perf-item--questions"><span className="pnldsb2-perf-val">{qStats?.totalResolved ?? 0}</span><span className="pnldsb2-perf-lbl">Questões total</span></div>
                    <div className="pnldsb2-perf-item pnldsb2-perf-item--questions"><span className="pnldsb2-perf-val">{qStats?.accuracy ?? 0}%</span><span className="pnldsb2-perf-lbl">Precisão (questões)</span></div>
                    <div className="pnldsb2-perf-item pnldsb2-perf-item--hl"><Star size={12} className="pnldsb2-perf-star" /><span className="pnldsb2-perf-val pnldsb2-perf-val--sm">{bestSubject ?? '—'}</span><span className="pnldsb2-perf-lbl">Ponto forte (questões)</span></div>
                    <div className="pnldsb2-perf-item pnldsb2-perf-item--hl"><Star size={12} className="pnldsb2-perf-star" /><span className="pnldsb2-perf-val pnldsb2-perf-val--sm">{bestDeck?.deckName ?? '—'}</span><span className="pnldsb2-perf-lbl">Melhor deck (flashcards)</span></div>
                </div>
            </div>

            {/* ── Consistência da Semana ────────────────────────────── */}
            <div className="pnldsb2-card">
                <div className="pnldsb2-card-header"><Activity size={15} /><span>Consistência da Semana</span></div>
                <div className="pnldsb2-week-wrap">
                    {weekHistory.map((day, i) => {
                        const h = weekMax > 0 ? (day.cardsStudied / weekMax) * 100 : 0;
                        const isToday = i === weekHistory.length - 1;
                        return (
                            <div key={i} className="pnldsb2-week-col">
                                <span className="pnldsb2-bar-count">{day.cardsStudied > 0 ? day.cardsStudied : ''}</span>
                                <div className="pnldsb2-bar-track">
                                    <div
                                        className={['pnldsb2-bar-fill', day.isProductiveDay ? 'pnldsb2-bar-fill--prod' : '', isToday ? 'pnldsb2-bar-fill--today' : '', day.cardsStudied === 0 ? 'pnldsb2-bar-fill--zero' : ''].join(' ')}
                                        style={{
                                            height: `${Math.max(h, day.cardsStudied > 0 ? 4 : 0)}%`,
                                            animationDelay: `${i * 0.06}s`,
                                        }}
                                    />
                                </div>
                                <span className={`pnldsb2-bar-label ${isToday ? 'pnldsb2-bar-label--today' : ''}`}>{day.dayLabel}</span>
                                {day.isProductiveDay && <span className="pnldsb2-bar-dot" title="Dia produtivo" />}
                            </div>
                        );
                    })}
                </div>
                <div className="pnldsb2-week-stats-row">
                    <div className="pnldsb2-week-stat"><span className="pnldsb2-week-stat-val">{weekSummary?.totalCards ?? 0}</span><span className="pnldsb2-week-stat-lbl">Total cards</span></div>
                    <div className="pnldsb2-week-stat"><span className="pnldsb2-week-stat-val">{weekSummary?.avgCardsPerDay ?? 0}</span><span className="pnldsb2-week-stat-lbl">Média cards/dia</span></div>
                    {weekSummary?.bestDay && (
                        <div className="pnldsb2-week-stat"><span className="pnldsb2-week-stat-val">{weekSummary.bestDay.dayLabel}</span><span className="pnldsb2-week-stat-lbl">Melhor dia ({weekSummary.bestDay.cards} cards)</span></div>
                    )}
                </div>
            </div>

            {/* ── Groups row ────────────────────────────────────────── */}
            <div className="pnldsb2-groups-row">
                <div className="pnldsb2-card pnldsb2-ranking-block">
                    <div className="pnldsb2-card-header">
                        <Trophy size={15} /><span>Ranking Global</span>
                        <button className="pnldsb2-card-link" onClick={() => guardedNavigate('groups', FEATURE_LABELS.groups, 'groups')}>Ver tudo <ChevronRight size={12} /></button>
                    </div>
                    {globalRanking.length === 0 ? (
                        <div className="pnldsb2-empty pnldsb2-empty--sm"><span>Sem grupos ainda</span></div>
                    ) : (
                        <div className="pnldsb2-ranking-list">
                            {globalRanking.map(g => (
                                <div key={g.groupId} className={`pnldsb2-ranking-item pnldsb2-ranking-item--${g.rank}`}>
                                    {rankIcon(g.rank)}
                                    <div className="pnldsb2-ranking-info">
                                        <span className="pnldsb2-ranking-name">{g.groupName}</span>
                                        <span className="pnldsb2-ranking-members"><Users size={11} /> {g.memberCount} {g.memberCount === 1 ? 'membro' : 'membros'}</span>
                                    </div>
                                    <span className="pnldsb2-ranking-score">{g.groupScore} pts</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="pnldsb2-card pnldsb2-mygroups-block">
                    <div className="pnldsb2-card-header">
                        <Users size={15} /><span>Meus Grupos</span>
                        <button className="pnldsb2-card-link" onClick={() => guardedNavigate('groups', FEATURE_LABELS.groups, 'groups')}>Gerenciar <ChevronRight size={12} /></button>
                    </div>
                    {myGroups.length === 0 ? (
                        <div className="pnldsb2-empty pnldsb2-empty--sm">
                            <span>Você não está em nenhum grupo</span>
                            <button className="pnldsb2-empty-cta" onClick={() => guardedNavigate('groups', FEATURE_LABELS.groups, 'groups')}>Criar ou entrar</button>
                        </div>
                    ) : (
                        <div className="pnldsb2-mygroups-list">
                            {myGroups.slice(0, 4).map(g => (
                                <div key={g._id} className="pnldsb2-mygroup-item">
                                    {g.groupIcon
                                        ? <img src={g.groupIcon || ''} alt="" className="pnldsb2-mygroup-icon" />
                                        : <div className="pnldsb2-mygroup-icon pnldsb2-mygroup-icon--ph"><Users size={14} /></div>
                                    }
                                    <div className="pnldsb2-mygroup-info">
                                        <span className="pnldsb2-mygroup-name">{g.name}</span>
                                        <span className="pnldsb2-mygroup-count">{g.members?.length ?? 0} membros</span>
                                    </div>
                                </div>
                            ))}
                            <button className="pnldsb2-prio-cta" onClick={() => guardedNavigate('groups', FEATURE_LABELS.groups, 'groups')}>
                                Ver ranking do grupo <ArrowRight size={13} />
                            </button>
                        </div>
                    )}
                </div>
            </div>
            </>
            )}
        </div>
    );
};

export default Painel;
